import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { request } from '../../utils/api';
import TimeCardTable from './TimeCardTable';
import TimeCardTableSkeleton from './TimeCardTableSkeleton';
import EditAttendanceModal from './EditAttendanceModal';
import SessionRemarkModal from './SessionRemarkModal';
import DateSlotNavigator from './DateSlotNavigator';
import useCurrentUser from '../../hooks/useCurrentUser';
import { PrintIcon } from '../../Icons';
import { generateTimeCardPrintHTML, openTimeCardPrintWindow } from '../../utils/timeCardPrint';
import { BRAND_THEME } from '../../ui/brandTheme';
import {
    INACTIVE_EMPLOYEE_PAYROLL_WRITE_MESSAGE,
    isEmployeeInactiveStatus,
} from '../../constants/employeeStatus';
import { SHOW_SESSION_REMARK_UI } from './sessionRemarkFeature';

const UserActivity = ({
    userId = null,
    employeeName: employeeNameProp = null,
    /** When true, show Print (Payroll time-card modal + admin only). Hidden on Dashboard Activity. */
    showPrintButton = false,
    /** Admin viewing another user's time card in Payroll — enables note delete + full edit access */
    isAdminView = false,
    /** Employee employment status — inactive users are read-only in Payroll. */
    employeeStatus = null,
    /** Embedded in Payroll modal — tighter layout, no duplicate user header */
    embedded = false,
}) => {
    const { officeLogo, companyName, fullName } = useCurrentUser();
    const canMutateTimeCard =
        Boolean(userId) && !isEmployeeInactiveStatus(employeeStatus);
    /** Remark edit/delete — UI gated by SHOW_SESSION_REMARK_UI (code kept for future). */
    const canEditSessionRemark =
        SHOW_SESSION_REMARK_UI &&
        !isEmployeeInactiveStatus(employeeStatus) &&
        (!userId || isAdminView);
    const canDeleteSessionRemark =
        SHOW_SESSION_REMARK_UI &&
        Boolean(userId) &&
        isAdminView &&
        !isEmployeeInactiveStatus(employeeStatus);
    // Calculate 15-day slots for a given month
    const getSlotsForMonth = (year, month) => {
        const daysInMonth = new Date(year, month, 0).getDate();
        const slots = [];
        
        // First slot: 1 to 15
        slots.push({
            start: new Date(year, month - 1, 1),
            end: new Date(year, month - 1, Math.min(15, daysInMonth))
        });
        
        // Second slot: 16 to end of month
        if (daysInMonth > 15) {
            slots.push({
                start: new Date(year, month - 1, 16),
                end: new Date(year, month - 1, daysInMonth)
            });
        }
        
        return slots;
    };

    // Get current slot based on today's date
    const getCurrentSlot = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth() + 1;
        const day = today.getDate();
        
        const slots = getSlotsForMonth(year, month);
        
        // Find which slot today falls into
        if (day <= 15) {
            return slots[0];
        } else {
            return slots[1] || slots[0];
        }
    };

    const [currentSlot, setCurrentSlot] = useState(() => {
        const slot = getCurrentSlot();
        return {
            start: slot.start,
            end: slot.end
        };
    });

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Edit Modal State
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingDay, setEditingDay] = useState(null);

    // Session remark modal (per session — not tied to clock in/out)
    const [remarkModalOpen, setRemarkModalOpen] = useState(false);
    const [editingRemarkSession, setEditingRemarkSession] = useState(null);

    /** Format date to YYYY-MM-DD (local only, no timezone conversion) */
    const formatDate = (date) => {
        if (!date) return '';
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };

    // Navigate to next/previous slot
    const handleSlotChange = (direction) => {
        const currentStart = new Date(currentSlot.start);
        const currentEnd = new Date(currentSlot.end);
        
        let newSlot;
        
        if (direction === 'next') {
            // Move to next slot
            const nextDate = new Date(currentEnd);
            nextDate.setDate(nextDate.getDate() + 1);
            const nextYear = nextDate.getFullYear();
            const nextMonth = nextDate.getMonth() + 1;
            const nextSlots = getSlotsForMonth(nextYear, nextMonth);
            
            // Find which slot the next date falls into
            const nextDay = nextDate.getDate();
            if (nextDay <= 15) {
                newSlot = nextSlots[0];
            } else {
                newSlot = nextSlots[1] || nextSlots[0];
            }
        } else {
            // Move to previous slot
            const prevDate = new Date(currentStart);
            prevDate.setDate(prevDate.getDate() - 1);
            const prevYear = prevDate.getFullYear();
            const prevMonth = prevDate.getMonth() + 1;
            const prevSlots = getSlotsForMonth(prevYear, prevMonth);
            
            // Find which slot the previous date falls into
            const prevDay = prevDate.getDate();
            if (prevDay <= 15) {
                newSlot = prevSlots[0];
            } else {
                newSlot = prevSlots[1] || prevSlots[0];
            }
        }
        
        setCurrentSlot({
            start: newSlot.start,
            end: newSlot.end
        });
    };

    const fetchActivity = async () => {
        if (!currentSlot.start || !currentSlot.end) return;
        
        setLoading(true);
        setError(null);
        setData(null);

        try {
            const startStr = formatDate(currentSlot.start);
            const endStr = formatDate(currentSlot.end);

            let response;
            
            if (userId) {
                // For specific user, use time-cards-list endpoint with userIds parameter
                // Pass userId as string in userIds parameter
                const url = `/attendance/time-cards-list?startDate=${startStr}&endDate=${endStr}&userIds=${userId}&page=1&limit=50`;
                response = await request(url);
                
                if (response.status === 1) {
                    const cards = response.result?.timeCards || [];
                    // Find the card for the specific userId
                    const userCard = cards.find(c => c.userId === userId);
                    if (userCard) {
                        setData(userCard);
                    } else {
                        // User exists but has no time card data
                        setData({ 
                            days: [], 
                            totalWorkTime: '00:00:00', 
                            totalBreakTime: '00:00:00',
                            totalDays: 0,
                            presentDays: 0,
                            userId: userId
                        });
                    }
                } else {
                    setError(response.message || 'Failed to fetch activity data');
                }
            } else {
                // For current user, use single time-card endpoint
                const url = `/attendance/time-card?startDate=${startStr}&endDate=${endStr}`;
                response = await request(url);
                
                if (response.status === 1) {
                    setData(response.result || { 
                        days: [], 
                        totalWorkTime: '00:00:00', 
                        totalBreakTime: '00:00:00',
                        totalDays: 0,
                        presentDays: 0
                    });
                } else {
                    setError(response.message || 'Failed to fetch activity data');
                }
            }
        } catch (err) {
            console.error("Error fetching time card:", err);
            let msg = "Failed to connect to the server.";
            if (err.message) msg = err.message;
            if (err.data && err.data.message) msg = err.data.message;
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch when slot changes
    useEffect(() => {
        if (currentSlot.start && currentSlot.end) {
            fetchActivity();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, currentSlot.start, currentSlot.end]);

    const handleEdit = (day) => {
        if (!canMutateTimeCard) {
            toast.error(INACTIVE_EMPLOYEE_PAYROLL_WRITE_MESSAGE);
            return;
        }
        setEditingDay(day);
        setEditModalOpen(true);
    };

    const handleAdd = (payload) => {
        if (!canMutateTimeCard) {
            toast.error(INACTIVE_EMPLOYEE_PAYROLL_WRITE_MESSAGE);
            return;
        }
        setEditingDay(payload);
        setEditModalOpen(true);
    };

    const handleEditSuccess = () => {
        fetchActivity();
    };

    const handleRemarkEdit = (session) => {
        if (!canEditSessionRemark) {
            toast.error(INACTIVE_EMPLOYEE_PAYROLL_WRITE_MESSAGE);
            return;
        }
        setEditingRemarkSession(session);
        setRemarkModalOpen(true);
    };

    const handleRemarkSuccess = () => {
        fetchActivity();
    };

    const handlePrintTimeCard = useCallback(() => {
        if (!data || loading) return;
        const name =
            employeeNameProp ||
            data.userName ||
            fullName ||
            "Employee";
        const html = generateTimeCardPrintHTML({
            companyName,
            officeLogo,
            employeeName: name,
            periodStart: currentSlot.start,
            periodEnd: currentSlot.end,
            summary: data,
            days: data.days || [],
        });
        openTimeCardPrintWindow(html);
    }, [
        data,
        loading,
        employeeNameProp,
        fullName,
        companyName,
        officeLogo,
        currentSlot.start,
        currentSlot.end,
    ]);

    const renderEmbeddedStats = () => {
        if (!data) return null;
        const items = [
            { label: 'Total days', value: data.totalDays || 0, className: 'text-slate-800' },
            { label: 'Present', value: data.presentDays || 0, className: 'text-emerald-600' },
            { label: 'Work', value: data.totalWorkTime || '00:00:00', className: 'text-indigo-600 font-mono' },
            { label: 'Break', value: data.totalBreakTime || '00:00:00', className: 'text-amber-600 font-mono' },
        ];
        return items.map((item) => (
            <span key={item.label} className="inline-flex items-baseline gap-1">
                <span className="text-slate-500">{item.label}:</span>
                <span className={`font-semibold tabular-nums ${item.className}`}>{item.value}</span>
            </span>
        ));
    };

    return (
        <div className={embedded ? 'space-y-2' : 'space-y-3'}>
            {/* Toolbar — full header on Dashboard; controls-only in Payroll modal */}
            <div
                className={`bg-white rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-2 ${
                    embedded ? 'px-3 py-2' : 'p-3 shadow-sm'
                } ${BRAND_THEME.section.wrap}`}
            >
                {!embedded ? (
                    <div className="min-w-0">
                        <h2 className={`text-base font-semibold ${BRAND_THEME.section.title}`}>
                            Monthly time card
                        </h2>
                        <p className="text-[13px] text-slate-500 mt-0.5">
                            View detailed attendance and break activity
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] min-w-0">
                        {renderEmbeddedStats()}
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                    {showPrintButton && (
                        <button
                            type="button"
                            onClick={handlePrintTimeCard}
                            disabled={loading || !data}
                            className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${BRAND_THEME.button.outline}`}
                            title="Open printable time card in a new window"
                        >
                            <PrintIcon size={14} className="shrink-0" />
                            Print
                        </button>
                    )}
                    <DateSlotNavigator
                        currentSlot={currentSlot}
                        onSlotChange={handleSlotChange}
                        loading={loading}
                        compact={embedded}
                    />
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 animate-fade-in">
                    <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p>{error}</p>
                    <button
                        onClick={() => setError(null)}
                        className="ml-auto text-red-500 hover:text-red-700"
                        aria-label="Dismiss error"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            )}

            {loading ? (
                <TimeCardTableSkeleton />
            ) : data ? (
                <div className="animate-fade-in">
                    <TimeCardTable
                        days={data.days || []}
                        summary={data}
                        onEdit={canMutateTimeCard ? handleEdit : null}
                        onAdd={canMutateTimeCard ? handleAdd : null}
                        onRemarkEdit={
                            SHOW_SESSION_REMARK_UI && canEditSessionRemark
                                ? handleRemarkEdit
                                : null
                        }
                        canEditRemark={
                            SHOW_SESSION_REMARK_UI && canEditSessionRemark
                        }
                        userName={data.userName}
                        embedded={embedded}
                    />
                </div>
            ) : (
                !error && (
                    <div
                        className={`text-center py-20 bg-white rounded-xl border border-dashed ${BRAND_THEME.section.wrap}`}
                    >
                        <div className="w-16 h-16 bg-indigo-50 text-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <h3 className={`text-lg font-medium mb-1 ${BRAND_THEME.section.title}`}>
                            No Data Displayed
                        </h3>
                        <p className="text-slate-500 max-w-sm mx-auto">
                            Use the navigation arrows to view different 15-day periods.
                        </p>
                    </div>
                )
            )}

            <EditAttendanceModal
                isOpen={editModalOpen && canMutateTimeCard}
                onClose={() => setEditModalOpen(false)}
                attendance={editingDay}
                onSuccess={handleEditSuccess}
                userId={userId}
            />

            {SHOW_SESSION_REMARK_UI ? (
                <SessionRemarkModal
                    isOpen={remarkModalOpen && canEditSessionRemark}
                    onClose={() => setRemarkModalOpen(false)}
                    session={editingRemarkSession}
                    date={editingRemarkSession?.date}
                    onSuccess={handleRemarkSuccess}
                    allowDelete={canDeleteSessionRemark}
                />
            ) : null}
        </div>
    );
};

export default UserActivity;
